import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BookingDialog from "@/components/BookingDialog";

const LOGO_URL = "/images/logo.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Servizi", path: "/servizi" },
  { name: "Chi Sono", path: "/chi-siamo" },
  { name: "Galleria", path: "/galleria" },
  { name: "Contatti", path: "/contatti" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass border-b border-white/5" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img
                src={LOGO_URL}
                alt="Kairos La Barberia"
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm uppercase tracking-[0.2em] font-medium transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-kairos-gold"
                      : "text-white/70 hover:text-white"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(" ", "-")}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="tel:+393517252089"
                className="text-white/70 hover:text-kairos-gold transition-colors"
                data-testid="nav-phone"
              >
                <Phone className="w-5 h-5" />
              </a>
              <button
                onClick={() => setBookingOpen(true)}
                className="bg-kairos-gold text-kairos-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-300"
                data-testid="nav-book-btn"
              >
                Prenota Ora
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden glass border-t border-white/5"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-8 space-y-6">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className={`block text-2xl font-serif ${
                        location.pathname === link.path
                          ? "text-kairos-gold"
                          : "text-white/80"
                      }`}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(" ", "-")}`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setBookingOpen(true);
                    }}
                    className="w-full bg-kairos-gold text-kairos-black py-4 text-sm uppercase tracking-[0.2em] font-bold mt-4"
                    data-testid="mobile-book-btn"
                  >
                    Prenota Ora
                  </button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </>
  );
};

export default Navbar;
